/**
 * Recondition, paid in wares only (economy plan SY-5b, direction §2.6, research D8, D-TBD): a **Serviceable**
 * component in the hold consumes goods from the hold and comes back at condition `bands.intact` (Intact), so it can
 * be installed (SY-5a/SY-5c). There is **no credit fee**: the bill is goods only, deterministic (no RNG).
 *
 * - **The bill** (`salvage.json recondition[type].goods`): units of each good per whole condition point, scaled by
 *   `bands.intact − condition`. A type without a bill is not reconditioned (only the installable Mk types have one:
 *   reactor, weapon, shield generator, thruster).
 * - **Money-press guard (R3-M3):** the bill priced at the strategic base price is at least the value the recondition
 *   adds × the best station component multiplier, so "buy goods → recondition → sell" never prints money. A data test
 *   in `recondition.test.ts` holds every bill to it; a tuning change that breaks it is fixed in the bill, never with a
 *   credit fee.
 * - **Goods flag (R3-H2):** the bill is paid in goods, which the player can only trade once `career.json
 *   goods.tradeEnabled` is on, so recondition refuses while it is off (like break-down).
 * - **Where:** stations with the `outfitting` service.
 * - **What:** Serviceable only. Wrecked refuses (break it down instead), Intact has nothing to recondition, and
 *   sealed, contract and story components refuse. Short goods refuse with "Needs 1.8 t Electronics" (the shortage
 *   card is Materials & Craft's). A refusal changes nothing.
 *
 * Pure: Career in, Career out.
 */
import { z } from 'zod'
import { componentTypeSchema, type ComponentType, type M2Data, type StationDef } from '../data/m2Schemas'
import { CONDITION_BAND, conditionBand, partValue } from '../salvage/generate'
import { EPSILON, GOOD_NAMES, withGood } from './ops'
import { GOOD_IDS, GOOD_MASS_T, goodIdSchema, goodT, type GoodId } from './parts'
import { fail, formatTonnes, ok, type CmdResult } from './result'
import type { CargoComponent, Career } from './types'

/** The strict form of `salvage.json recondition`: only component types, only goods, positive units. */
export const reconditionTableSchema = z.partialRecord(
  componentTypeSchema,
  z.strictObject({ goods: z.partialRecord(goodIdSchema, z.number().positive()) }),
)
export type ReconditionTable = z.infer<typeof reconditionTableSchema>

export const RECONDITION_TEXTS = {
  closed: 'Reconditioning is not open yet.',
  noService: '{station} has no outfitting service.',
  notInCargo: 'That component is not in the hold.',
  contract: 'Contract and story cargo cannot be reconditioned.',
  noBill: 'That component type cannot be reconditioned.',
  intact: 'It is already Intact.',
  wrecked: 'It is too far gone to recondition. Break it down instead.',
  short: 'Needs {list}.',
} as const

/** One good of a recondition bill. */
export interface ReconditionLine {
  good: GoodId
  /** Units the bill takes (`cargo-parts.json massT` tonnes each). */
  qty: number
  /** Units the hold carries now. */
  held: number
}

type ReconditionData = Pick<M2Data, 'salvage'>

/**
 * The goods it takes to bring `component` up to `bands.intact` (in goods order; zero lines dropped), or null when its
 * type has no bill. Empty for a component already Intact.
 */
export function reconditionLines(
  career: Career,
  data: ReconditionData,
  component: Pick<CargoComponent, 'type' | 'condition'>,
): ReconditionLine[] | null {
  const bill = data.salvage.recondition[component.type]
  if (bill === undefined) return null
  const gap = Math.max(0, data.salvage.bands.intact - Math.min(1, Math.max(0, component.condition)))
  const goods: Readonly<Partial<Record<string, number>>> = bill.goods
  const out: ReconditionLine[] = []
  for (const good of GOOD_IDS) {
    const qty = (goods[good] ?? 0) * gap
    if (qty > EPSILON) out.push({ good, qty, held: goodT(career.cargo.parts, good) })
  }
  return out
}

/** "1.8 t Electronics, 0.6 t Drive parts" (the outfitting bill). */
export function reconditionLabel(lines: readonly Pick<ReconditionLine, 'good' | 'qty'>[]): string {
  return lines.map((l) => `${formatTonnes(l.qty * GOOD_MASS_T[l.good])} t ${GOOD_NAMES[l.good]}`).join(', ')
}

/** The shortfall lines of a bill (what the hold lacks), as `reconditionLabel` reads them. */
export function reconditionShortfall(lines: readonly ReconditionLine[]): Pick<ReconditionLine, 'good' | 'qty'>[] {
  const out: Pick<ReconditionLine, 'good' | 'qty'>[] = []
  for (const l of lines) if (l.qty > l.held + EPSILON) out.push({ good: l.good, qty: l.qty - l.held })
  return out
}

/** Component types `salvage.json recondition` has a bill for. */
export function reconditionTypes(data: ReconditionData): ComponentType[] {
  return Object.keys(data.salvage.recondition).filter((t): t is ComponentType => componentTypeSchema.safeParse(t).success)
}

/**
 * Reconditions the hold component `componentId` at `station`: takes the bill's goods from the hold and sets the
 * component to condition `bands.intact` (its value follows, its Mk and mass stay). Refuses (Career untouched) while
 * `goods.tradeEnabled` is off, at a station without outfitting, for a component not in the hold, a sealed, contract or
 * story component, a type with no bill, a component that isn't Serviceable, or goods short of the bill.
 */
export function reconditionComponent(
  career: Career,
  data: Pick<M2Data, 'career' | 'salvage' | 'wares'>,
  station: StationDef,
  componentId: string,
): CmdResult<Career> {
  if (!data.career.goods.tradeEnabled) return fail('serviceUnavailable', RECONDITION_TEXTS.closed)
  if (!station.services.includes('outfitting')) {
    return fail('serviceUnavailable', RECONDITION_TEXTS.noService.replace('{station}', station.name), { station: station.id })
  }
  const component = career.cargo.components.find((c) => c.id === componentId)
  if (component === undefined) return fail('notInCargo', RECONDITION_TEXTS.notInCargo, { id: componentId })
  if (component.sealed === true || component.contractId !== undefined || component.story === true) {
    return fail('notBoughtHere', RECONDITION_TEXTS.contract, { id: component.id })
  }
  const lines = reconditionLines(career, data, component)
  if (lines === null) return fail('notBoughtHere', RECONDITION_TEXTS.noBill, { id: component.id })
  const band = conditionBand(component.condition, data.salvage.bands)
  if (band === CONDITION_BAND.intact) return fail('nothingToRepair', RECONDITION_TEXTS.intact, { id: component.id })
  if (band === CONDITION_BAND.wrecked) return fail('notBoughtHere', RECONDITION_TEXTS.wrecked, { id: component.id })
  const short = reconditionShortfall(lines)
  if (short.length > 0) {
    return fail('notInCargo', RECONDITION_TEXTS.short.replace('{list}', reconditionLabel(short)), { id: component.id, good: short[0].good })
  }
  let next = career
  for (const l of lines) next = withGood(next, l.good, -(l.qty < l.held ? l.qty : l.held))
  const condition = data.salvage.bands.intact
  const restored: CargoComponent = { ...component, condition, value: partValue(component.type, condition, data) }
  const components = next.cargo.components.map((c) => (c.id === component.id ? restored : c))
  return ok({ ...next, cargo: { ...next.cargo, components } })
}
